import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "~/components/ui";
import { useCompoundRecordVersionCreate } from "~/features/compoundRecordVersions/hooks";
import type { CompoundRecordVersionFormData } from "~/features/compoundRecordVersions/types";
import { validateVersionFormat } from "~/features/compoundRecordVersions/utils/version";

interface CompoundRecordVersionCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CompoundRecordVersionCreateDialog({ open, onOpenChange }: CompoundRecordVersionCreateDialogProps) {
  const { t } = useTranslation();
  const form = useForm<CompoundRecordVersionFormData>({ defaultValues: { version: "" } });
  const { submit, processing } = useCompoundRecordVersionCreate({
    onSuccess: () => {
      form.reset();
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("ui.addX", { x: t("model.compoundRecordVersion.modelName") })}</DialogTitle>
          <DialogDescription>{t("feature.compoundRecordVersions.create.versionFormatDescription")}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="space-y-4">
            <FormField
              control={form.control}
              name="version"
              rules={{
                required: t("ui.required"),
                validate: (value) =>
                  validateVersionFormat(value) || t("feature.compoundRecordVersions.create.versionFormatError"),
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("ui.version")}</FormLabel>
                  <FormControl>
                    <Input placeholder="v20241008" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={processing}>
                {t("ui.save")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
